import { computed, type ComputedRef } from 'vue';
import { useMainStore } from '../stores/mainStore.js';

interface StyleTag {
  innerHTML: string;
  type: string;
}

interface HeaderCriticalCssResult {
  newMobileHeaderActive: ComputedRef<boolean>;
  styleTags: StyleTag[];
  mobileBreakpoint: number;
}

/**
 * Injects critical CSS server-side to prevent header FOUC:
 * 1. Hides old desktop header/nav at mobile widths when the new mobile header is active.
 * 2. Reserves vertical space for `#view-header` in mobile views. Larger than mobile view gets #view-header { `min-height:unset` }
 *    via media query — SSR has no viewport, so the browser picks the right branch at first paint.
 */
export function useHeaderCriticalCss(): HeaderCriticalCssResult {
  const mainStore = useMainStore();
  const headerVersion: string = mainStore.theme?.header?.options?.version ?? '';
  const menuVersion: string = mainStore.theme?.menu?.options?.version ?? '';
  const isBarNav = menuVersion === 'M4' || menuVersion === 'M5';
  const isH5OrH6 = headerVersion === 'H5' || headerVersion === 'H6';
  const mobileBreakpoint = isH5OrH6 ? 600 : isBarNav ? 1023 : 960;
  const styleTags: StyleTag[] = [];
  const newMobileHeaderFeatureFlagEnabled = computed(() => !!mainStore.featureFlags?.cms_new_mobile_header_enabled && (headerVersion === 'H1' || headerVersion === 'H2'));
  const newMobileHeaderActive = computed(
    () => headerVersion === 'H3' || newMobileHeaderFeatureFlagEnabled.value,
  );
  // H5 and H6 switch to mobile at 600px (narrower than the default 960px), matching CmsHeader's isMobile logic.
  if (headerVersion === 'H4') {
    // H4 on homepage sets position:absolute on #cms-header (overlay on gallery images).
    // The JS condition `!isMobile` was removed so it no longer flips after mount.
    // This rule restores position:relative on mobile so the header stays in flow.
    styleTags.push({
      innerHTML: `@media (max-width:${mobileBreakpoint}px){#cms-header:has(.h4){position:relative!important;width:auto!important}}`,
      type: 'text/css',
    });
  }
  if (headerVersion === 'H7') {
    styleTags.push({
      innerHTML: `@media (max-width:${mobileBreakpoint}px){.h7 > .top-bar,.h7 .header-container{display:none!important}}` +
        `@media (min-width:${mobileBreakpoint + 1}px){.h7 .mobile-header{display:none!important}}`,
      type: 'text/css',
    });
  }
  if (headerVersion === 'H8') {
    styleTags.push({
      innerHTML: `@media (max-width:${mobileBreakpoint}px){.h8 > .top-bar,.h8 .header-container{display:none!important}}` +
        `@media (min-width:${mobileBreakpoint + 1}px){.h8 .mobile-header{display:none!important}}`,
      type: 'text/css',
    });
  }
  if (headerVersion === 'H5') {
    styleTags.push({
      innerHTML: `@media (max-width:${mobileBreakpoint}px){.h5 .header-container,.h5 > .bottom-bar{display:none!important}}` +
        `@media (min-width:${mobileBreakpoint + 1}px){.h5 .mobile-header{display:none!important}}`,
      type: 'text/css',
    });
  }
  if (headerVersion === 'H6') {
    styleTags.push({
      innerHTML: `@media (max-width:${mobileBreakpoint}px){.h6 .header-container{display:none!important}}` +
        `@media (min-width:${mobileBreakpoint + 1}px){.h6 .mobile-header{display:none!important}}`,
      type: 'text/css',
    });
  }

  if (newMobileHeaderActive.value) {
    if (isBarNav) {
      // M4/M5 bar nav renders at the CmsHeader level (outside the header version component) and
      // uses !isMobile — suppress the flash before screenSize resolves after mount.
      styleTags.push({
        innerHTML: `@media (max-width:${mobileBreakpoint}px){#m4,#m5{display:none!important}}`,
        type: 'text/css',
      });
    }
    if (headerVersion === 'H1') {
      styleTags.push({
        innerHTML: `@media (max-width:${mobileBreakpoint}px){#h1 .header-container{display:none!important}}` +
          `@media (min-width:${mobileBreakpoint + 1}px){#h1 .mobile-header{display:none!important}}`,
        type: 'text/css',
      });
    }
    if (headerVersion === 'H2') {
      // Utility bar renders right-aligned on first paint (screenSize initialises as 0);
      // pre-centering avoids the visible shift when isMobile resolves after mount.
      styleTags.push({
        innerHTML: `@media (max-width:${mobileBreakpoint}px){#h2 .header-container,#h2 [data-testid="small-utilities-bar"] .search{display:none!important}` +
          `#h2 [data-testid="small-utilities-bar"] .utility-bar-items{justify-content:center}}` +
          `@media (min-width:${mobileBreakpoint + 1}px){#h2 .mobile-header{display:none!important}}`,
        type: 'text/css',
      });
    }
    if (headerVersion === 'H3') {
      styleTags.push({
        innerHTML: `@media (max-width:${mobileBreakpoint}px){#h3 .utility-menu{display:none!important}}` +
          `@media (min-width:${mobileBreakpoint + 1}px){#h3 .mobile-header{display:none!important}}`,
        type: 'text/css',
      });
    }
  }

  // H4 excluded: position:absolute overlay on desktop, custom mobile transition — no reservation needed.
  // if (newMobileHeaderActive.value && headerVersion !== 'H4') {
  //   const hasUtilities = (mainStore.homepage?.header?.links?.length ?? 0) > 0;
  //   const mobileHeights: Record<string, number> = { H1: 98, H3: 64, H7: 128, H8: 128 };
  //   const mobileHeight = mobileHeights[headerVersion] ?? (hasUtilities ? 218 : 163);
  //   styleTags.push({
  //     innerHTML: `@media(max-width:${mobileBreakpoint}px){#view-header{min-height:${mobileHeight}px}}`,
  //     type: 'text/css',
  //   });
  // }

  return { newMobileHeaderActive, styleTags, mobileBreakpoint };
}
